import { z } from "zod";

// ---------------------------------------------------------------------------
// 入金と消込（部品 payments。営業力 E1、../eigyouryoku/docs/08_入金と消込.md）。
//   ・入金は顧客の単位で登録し、請求書に配分する（決めごと1）。配分の既定は お支払期限の古い順に上から埋める
//   ・残＝請求額（税込）− 配分（現金＋手数料として処理＋値引きとして処理）。DB のビュー v_invoice_balance が正（保存しない）
//   ・門は API の requireModule('sales')。sales が OFF の会社には出ない（2026-09-29 から現場力の会社も既定で ON）
//   ・文はここに1か所だけ（画面と API が同じ文を出す）。{customer} は会社の言葉に置き換わる
// ---------------------------------------------------------------------------

/** 入金の方法（決めごと6。相殺は第1版は名前だけ） */
export const PaymentMethod = z.enum(["bank_transfer", "cash", "card", "offset", "other"]);
export type PaymentMethod = z.infer<typeof PaymentMethod>;
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  bank_transfer: "振込",
  cash: "現金",
  card: "カード",
  offset: "相殺",
  other: "その他",
};

/** 請求書の入金の状態（決めごと9。documents.status は issued のまま） */
export const InvoiceState = z.enum(["unpaid", "partial", "paid", "overdue"]);
export type InvoiceState = z.infer<typeof InvoiceState>;
export const INVOICE_STATE_LABEL: Record<InvoiceState, string> = {
  unpaid: "未入金",
  partial: "一部入金",
  // #321（11/5）：「入金済み」→「入金済」（タブと同じ）
  paid: "入金済",
  overdue: "期限超過",
};

const DAY = 24 * 60 * 60 * 1000;
/** "YYYY-MM-DD" どうしの日数の差（a − b）。時差でずれないよう UTC の0時で数える */
export function daysBetween(a: string, b: string): number {
  const t = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.round((t(a) - t(b)) / DAY);
}

/**
 * 残と状態（DB の v_invoice_balance と同じ式。画面の試算と、テストの期待値に使う）。
 *   残 ≤ 0 → paid ／ 残 > 0 かつ お支払期限 < 今日 → overdue ／ 何か当たっていれば partial ／ それ以外 unpaid
 */
export function invoiceStateOf(x: {
  total: number;
  allocated: number;
  fee?: number;
  discount?: number;
  dueDate: string | null;
  today: string;
}): { balance: number; state: InvoiceState; overdueDays: number } {
  const used = x.allocated + (x.fee ?? 0) + (x.discount ?? 0);
  const balance = x.total - used;
  if (balance <= 0) return { balance, state: "paid", overdueDays: 0 };
  if (x.dueDate && x.dueDate < x.today) return { balance, state: "overdue", overdueDays: daysBetween(x.today, x.dueDate) };
  return { balance, state: used > 0 ? "partial" : "unpaid", overdueDays: 0 };
}

/** 「期限超過 12日」（色だけで伝えない。文字で出す） */
export function overdueText(days: number): string {
  return `期限超過 ${days}日`;
}

/**
 * 入金のある請求書の取消（#173）：「⋯」の取消を薄く出して押せなくし、下に出す1行。API の断り（unissueHasPayment）も同じ文で終わる
 * ＝画面と API が同じ言い方（入金の画面のボタンは「入金削除」）
 */
export const UNISSUE_AFTER_PAYMENT_VOID = "入金を削除すると取消できます";

export const PAYMENT_MSG = {
  notFound: "その入金は見つかりません",
  amountInvalid: "入金額は 1円以上で入力してください",
  amountFixed: "入金額と{customer}は変更できません。削除して登録し直してください",
  futureDate: "入金日は本日までの日付で入力してください",
  methodInvalid: "入金の方法を選んでください",
  partnerRequired: "{customer}を選んでください",
  partnerNotFound: "その{customer}は見つかりません",
  overAllocated: "配分の合計が入金額を超えています。配分を減らしてください",
  overInvoice: "請求書の残額を超えて消込しています。配分を減らしてください",
  discountOverLimit: (limit: number) => `値引きとして処理できるのは ${limit.toLocaleString("ja-JP")}円までです（設定 → 会社情報）`,
  invoiceNotFound: "その請求書は見つかりません",
  invoiceNotIssued: "入金は発行済みの請求書にのみ消込できます",
  invoiceOtherClient: "ほかの{customer}の請求書には消込できません",
  allocInvalid: "配分の形が正しくありません（同じ請求書が2回ある、または負の額）",
  voided: "この入金は削除済です",
  hasReceipt: "この入金から作成した領収書があります。先に領収書を取消す（下書きなら削除する）と、入金を削除できます",
  hasReceiptAllocations: "この入金から作成した領収書があるので、配分は変更できません。先に領収書を取消してください",
  created: "入金を登録しました",
  updated: "入金を修正しました",
  voidedDone: "入金を削除しました。請求書の残額に戻ります",
  noAllocationForReceipt: "未消込の入金からは領収書を作成できません。先に請求書に消込してください",
  unissueHasPayment: `入金の記録がある請求書は取消できません。${UNISSUE_AFTER_PAYMENT_VOID}`,
  invoiceAlreadyPaid: "この請求書には入金の記録があります。入金の画面から領収書を作成してください",
  /** 差額のボタン（決めごと3・4） */
  feeButton: (yen: number) => `差額 ${yen.toLocaleString("ja-JP")}円を 手数料として処理`,
  discountButton: (yen: number) => `差額 ${yen.toLocaleString("ja-JP")}円を 値引きとして処理`,
} as const;

/** 配分の1行（登録・直すときに送る）。0 の行は作らない（DB が落とす） */
export const PaymentAllocationInput = z.object({
  documentId: z.string().uuid(),
  amount: z.number().int().min(0, PAYMENT_MSG.allocInvalid),
  fee: z.number().int().min(0, PAYMENT_MSG.allocInvalid).default(0),
  discount: z.number().int().min(0, PAYMENT_MSG.allocInvalid).default(0),
});
export type PaymentAllocationInput = z.infer<typeof PaymentAllocationInput>;

const Ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "日付の形で入力してください");

/** POST /payments */
export const PaymentCreate = z.object({
  partnerId: z.string().uuid(PAYMENT_MSG.partnerRequired),
  receivedOn: Ymd,
  amount: z.number().int(PAYMENT_MSG.amountInvalid).min(1, PAYMENT_MSG.amountInvalid).max(99_999_999_999, PAYMENT_MSG.amountInvalid),
  method: PaymentMethod.default("bank_transfer"),
  memo: z.string().trim().max(1000).optional(),
  allocations: z.array(PaymentAllocationInput).max(200).default([]),
});
export type PaymentCreate = z.infer<typeof PaymentCreate>;

/** PUT /payments/:id。額と顧客は変えない（送って違えば 400 PAYMENT_AMOUNT_FIXED）。省いた欄は変えない。allocations を送れば入れ替え */
export const PaymentUpdate = z.object({
  receivedOn: Ymd.optional(),
  method: PaymentMethod.optional(),
  memo: z.string().trim().max(1000).nullable().optional(),
  allocations: z.array(PaymentAllocationInput).max(200).optional(),
  amount: z.number().optional(),
  partnerId: z.string().optional(),
});
export type PaymentUpdate = z.infer<typeof PaymentUpdate>;

/** POST /payments/suggest */
export const PaymentSuggestRequest = z.object({
  partnerId: z.string().uuid(PAYMENT_MSG.partnerRequired),
  amount: z.number().int().min(0).default(0),
});
export type PaymentSuggestRequest = z.infer<typeof PaymentSuggestRequest>;

/** 残のある請求書（配分の候補）。suggested＝上から埋めた案 */
export const OpenInvoice = z.object({
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  subject: z.string(),
  issueDate: z.string(),
  dueDate: z.string().nullable(),
  total: z.number().int(),
  balance: z.number().int(),
  state: InvoiceState,
  overdueDays: z.number().int(),
  suggested: z.number().int(),
});
export type OpenInvoice = z.infer<typeof OpenInvoice>;
export const OpenInvoiceList = z.object({ items: z.array(OpenInvoice), unallocated: z.number().int() });
export type OpenInvoiceList = z.infer<typeof OpenInvoiceList>;

/** 入金の配分（読むとき）。請求書の今の残・状態つき */
export const PaymentAllocation = z.object({
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  subject: z.string(),
  dueDate: z.string().nullable(),
  invoiceTotal: z.number().int(),
  amount: z.number().int(),
  fee: z.number().int(),
  discount: z.number().int(),
  /** 請求書の今の残（ほかの入金も含めて） */
  balance: z.number().int(),
  state: InvoiceState,
});
export type PaymentAllocation = z.infer<typeof PaymentAllocation>;

export const Payment = z.object({
  id: z.string().uuid(),
  partner: z.object({ id: z.string().uuid(), name: z.string(), honorific: z.string() }),
  receivedOn: z.string(),
  amount: z.number().int(),
  method: PaymentMethod,
  memo: z.string(),
  /** 現金で当てた合計 */
  allocated: z.number().int(),
  /** まだ当てていない額（前受金・過入金） */
  unallocated: z.number().int(),
  allocations: z.array(PaymentAllocation),
  /** この入金から起こした領収書（1入金＝1領収書）。無ければ null */
  receipt: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), status: z.string() }).nullable(),
  voidedAt: z.string().nullable(),
  createdAt: z.string(),
  createdByName: z.string(),
  /** 会社の値引きの上限（画面の「値引きとして処理」の判定に） */
  shortfallDiscountLimit: z.number().int(),
});
export type Payment = z.infer<typeof Payment>;

export const PaymentTab = z.enum(["unallocated", "month", "all"]);
export type PaymentTab = z.infer<typeof PaymentTab>;
export const PAYMENT_TAB_LABEL: Record<PaymentTab, string> = {
  // #302（A4）：「まだ当てていない額」→「未消込額」に合わせてタブも「未消込」
  unallocated: "未消込",
  month: "今月",
  all: "すべて",
};

export const PaymentSummary = z.object({
  id: z.string().uuid(),
  partnerId: z.string().uuid(),
  /** 一覧の名前：略称（空なら正式名称。#302 A2）。乗せたときは partnerFullName */
  partnerName: z.string(),
  partnerFullName: z.string().optional(),
  receivedOn: z.string(),
  amount: z.number().int(),
  method: PaymentMethod,
  /** 配分先（「I-0002 ほか1件」）。無ければ空 */
  allocatedTo: z.string(),
  invoiceCount: z.number().int(),
  unallocated: z.number().int(),
  voided: z.boolean(),
  hasReceipt: z.boolean(),
});
export type PaymentSummary = z.infer<typeof PaymentSummary>;
export const PaymentList = z.object({
  tab: PaymentTab,
  items: z.array(PaymentSummary),
  /** タブの数（まだ消し込んでいない／今月／すべて） */
  counts: z.object({ unallocated: z.number().int(), month: z.number().int(), all: z.number().int() }),
  /** まだ消し込んでいない額の合計 */
  unallocatedTotal: z.number().int(),
  /**
   * #302（A6）：絞った結果（タブ・期間・{customer}・検索）の件数・入金額・未消込額。タブの数（counts）と上の2つも同じ絞りをかけた値。
   * 題の右の「未消込 n件 ○円」は絞りに関わらず会社の全部＝unallocatedAll
   */
  sum: z.object({ count: z.number().int(), amount: z.number().int(), unallocated: z.number().int() }).optional(),
  unallocatedAll: z.object({ count: z.number().int(), total: z.number().int() }).optional(),
});
export type PaymentList = z.infer<typeof PaymentList>;

/** 請求書の画面の「入金の記録」の帯（sales の会社だけ） */
/** 回ごとの残（#267 3b。DB の v_payment_schedule_balance＝入金を期日の古い順に当てたとみなす。docs/97 ★6） */
export const PayScheduleRow = z.object({
  seq: z.number().int(),
  count: z.number().int(),
  dueOn: z.string().nullable(),
  amount: z.number().int(),
  paid: z.number().int(),
  balance: z.number().int(),
  state: InvoiceState,
  overdueDays: z.number().int(),
});
export type PayScheduleRow = z.infer<typeof PayScheduleRow>;

/** 請求書の「お支払い予定」（#267 3b。分割払いの請求書だけ） */
export const InvoiceSchedule = z.object({
  count: z.number().int(),
  intervalMonths: z.number().int(),
  rows: z.array(PayScheduleRow),
});
export type InvoiceSchedule = z.infer<typeof InvoiceSchedule>;

export const InvoicePayments = z.object({
  total: z.number().int(),
  allocated: z.number().int(),
  fee: z.number().int(),
  discount: z.number().int(),
  balance: z.number().int(),
  state: InvoiceState,
  overdueDays: z.number().int(),
  rows: z.array(
    z.object({
      paymentId: z.string().uuid(),
      receivedOn: z.string(),
      method: PaymentMethod,
      amount: z.number().int(),
      fee: z.number().int(),
      discount: z.number().int(),
    }),
  ),
  /** 残のある最初の回の期日（#267 3b。分割払いでなければお支払期限）。期限超過のチップの日付 */
  nextDueDate: z.string().nullable().optional(),
  /** 分割払いの請求書の「お支払い予定」（#267 3b）。分割払いでなければ null */
  schedule: InvoiceSchedule.nullable().optional(),
});
export type InvoicePayments = z.infer<typeof InvoicePayments>;

/** 請求書の一覧のタブ（sales の会社だけ。sales が OFF の会社はタブを出さない） */
export const InvoicePayTab = z.enum(["all", "unpaid", "overdue", "paid"]);
export type InvoicePayTab = z.infer<typeof InvoicePayTab>;
export const INVOICE_PAY_TAB_LABEL: Record<InvoicePayTab, string> = {
  all: "すべて",
  unpaid: "未入金",
  overdue: "期限超過",
  // #302（A4）：タブは「入金済」（#321 から状態の札も「入金済」）
  paid: "入金済",
};

/** ホームのカード「入金待ち」（決めごと13：期限超過が上・次に期限の近い順） */
export const AwaitingPayment = z.object({
  count: z.number().int(),
  total: z.number().int(),
  overdueCount: z.number().int(),
  overdueTotal: z.number().int(),
  /** まだ消し込んでいない入金（宙に浮いたもの。ルール9） */
  unallocatedCount: z.number().int(),
  unallocatedTotal: z.number().int(),
  rows: z.array(
    z.object({
      documentId: z.string().uuid(),
      documentNo: z.string().nullable(),
      partnerId: z.string().uuid(),
      clientName: z.string(),
      subject: z.string(),
      dueDate: z.string().nullable(),
      balance: z.number().int(),
      state: InvoiceState,
      overdueDays: z.number().int(),
      /** 分割払いの回（#267 3b：行は回ごと。分割払いでなければ 1／1） */
      seq: z.number().int().default(1),
      ofCount: z.number().int().default(1),
    }),
  ),
  /** 残のある回の数（#267 3b。分割払いは回ごとに1行。行が rows より多ければ「すべて表示」） */
  rowCount: z.number().int().optional(),
});
export type AwaitingPayment = z.infer<typeof AwaitingPayment>;

/** 今月の数字の「未入金」（今の残の合計と、そのうち期限超過） */
export const UnpaidFigures = z.object({ count: z.number().int(), total: z.number().int(), overdueTotal: z.number().int() });
export type UnpaidFigures = z.infer<typeof UnpaidFigures>;

/** 案件の画面の「入金」タブの1行（この案件の請求書に配分された入金） */
export const GroupPaymentRow = z.object({
  paymentId: z.string().uuid(),
  receivedOn: z.string(),
  method: PaymentMethod,
  documentId: z.string().uuid(),
  documentNo: z.string().nullable(),
  amount: z.number().int(),
  fee: z.number().int(),
  discount: z.number().int(),
});
export type GroupPaymentRow = z.infer<typeof GroupPaymentRow>;
